import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Building, Plus, Check, ChevronDown, PlusCircle, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useAuth } from '@/components/AuthProvider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
const business_dropdown_icon = '/assets/business-dropdown-icon.svg';

interface HeaderBusinessSelectorProps {
  selectedAccountId?: string;
  onAccountChange: (accountId: string) => void;
}

export function HeaderBusinessSelector({ selectedAccountId, onAccountChange }: HeaderBusinessSelectorProps) {
  const { data: accounts, isLoading } = useBusinessAccounts();
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();
  const { data: entitlements } = useOrganizationEntitlements();
  const { user, signOut } = useAuth();

  // Check if user can add more accounts
  const canAddAccount = React.useMemo(() => {
    if (!entitlements?.entitlements?.limits) return false;

    const limit = entitlements.entitlements.limits.business_accounts;
    // -1 means unlimited
    if (limit === -1) return true;

    // Check if current usage is less than limit
    // Note: accounts?.length might be undefined initially, treat as 0
    return (accounts?.length || 0) < (limit || 0);
  }, [entitlements, accounts]);

  const handleAddBusinessAccount = () => {
    interceptedNavigate('/home/business-accounts');
  };

  const handleSignOut = async () => {
    await signOut?.();
    interceptedNavigate('/auth');
  };

  const handleProfileSettings = () => {
    interceptedNavigate('/home/settings');
  };

  const handleTeamSettings = () => {
    interceptedNavigate('/home/team-management');
  };

  // Get user initials for avatar
  const getUserInitials = useMemo(() => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  // Auto-select / persistence is owned by BusinessAccountContext — don't
  // duplicate it here. Falling back to accounts[0] for display would also
  // mask a missing/null context selection and silently overwrite the user's
  // pick on refresh.
  const selectedAccount = accounts?.find(account => account.id === selectedAccountId) || null;

  // Check if the selected account is unlinked
  const isUnlinked = selectedAccount && (
    selectedAccount.is_verified === false ||
    !selectedAccount.business_account_id ||
    !selectedAccount.phone_number_id
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md">
        <Building className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // Always display user info in the main trigger, regardless of accounts
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          className="flex items-center gap-2 ml-[10px] md:ml-[0px] cursor-pointer hover:opacity-80 transition-opacity outline-none"
          data-tour="business-account-selector"
          data-testid="user-menu-trigger"
        >
          <Avatar className="h-[40px] w-[40px] !rounded-[5px] flex-shrink-0">
            <AvatarImage src={user?.avatar_url} alt={user?.full_name || user?.email} />
            <AvatarFallback className="text-sm font-semibold !rounded-[5px] bg-gradient-to-br from-gray-100 to-gray-200">
              {getUserInitials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:flex flex-col items-start min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-[14px] text-[#08090A] max-w-[110px] md:max-w-[140px] lg:max-w-[200px] xl:max-w-[300px] truncate font-medium ">
                {user?.full_name || user?.email || 'User'}
              </span>
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            </div>
            <div className="hidden md:flex items-center gap-2">
              <span className="text-[12px] text-[#08090A] truncate max-w-[140px] lg:max-w-[200px]">
                {selectedAccount?.phone_number || 'No connected number'}
              </span>
            </div>
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[240px]">
        <DropdownMenuLabel>Business Accounts</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20">
          {!accounts || accounts.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
              No business accounts connected
            </div>
          ) : (
            accounts.map((account) => (
              <DropdownMenuItem
                key={account.id}
                onClick={() => onAccountChange(account.id)}
                className="flex items-center justify-between cursor-pointer"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-[13px]">{account.name}</span>
                  <span className="text-[11px] text-muted-foreground">{account.phone_number}</span>
                </div>
                {selectedAccount?.id === account.id && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>
        {canAddAccount && accounts && accounts.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleAddBusinessAccount}
              className="cursor-pointer gap-2 text-muted-foreground hover:text-foreground"
              style={{ background: 'var(--main-color)', color: 'white' }}
            >
              <PlusCircle className="h-4 w-4" />
              <span>Add another account</span>
            </DropdownMenuItem>
          </>
        )}
        {(!accounts || accounts.length === 0) && canAddAccount && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleAddBusinessAccount}
              className="cursor-pointer gap-2 text-muted-foreground hover:text-foreground"
            >
              <PlusCircle className="h-4 w-4" />
              <span>Add business account</span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleProfileSettings}
          className="cursor-pointer focus:bg-accent transition-colors duration-150"
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={handleSignOut}
          className="cursor-pointer text-destructive focus:text-destructive focus:bg-red-50 dark:focus:bg-red-950/20"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
